import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Input, notification, Spin, Pagination, InputNumber } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Loader2, Save, Users } from 'lucide-react';
import moment from 'moment';
import { connect } from 'react-redux';
import get from 'lodash/get';
import {
  fetchTechnicianLog,
  updateTechnicianLog,
  updateDsrLogSections,
} from '../../redux/dsr/action';
import { addRole, fetchRoles } from '../../redux/roles/action';
import {
  buildDsrTechnicianRoleOptions,
  getDsrTechnicianAllowedRoles,
} from '../../constants/dprPersonnelRoleGroups';
import TechnicianLogCustomSelect from './TechnicianLogCustomSelect';
import './TechnicianLogSection.css';

const { TextArea } = Input;

const STATUS_SELECT_OPTIONS = [
  'Select Status',
  'Mainline',
  'Offline',
  'Offshore',
  'Tie-In',
  'Standby',
  'Mobilize',
  'De-Mobilize',
];

const TechnicianLogSection = ({
  shift,
  selectedDate,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSkipped,
  isSubmitted,
  onComplete,
  onCancel,
  isShiftSkipped = false,
  onSkipChange,
  onFetchTechnicianLog,
  onUpdateTechnicianLog,
  onUpdateDsrLogSections,
  isTechnicianLogLoading,
  isUpdatingTechnicianLog,
  currentTechnicianLog,
  globalRoles,
  onAddRole,
  onFetchRoles,
}) => {
  const [rowData, setRowData] = useState([]);
  const [comments, setComments] = useState('');
  const [skipped, setSkipped] = useState(false);
  const [paginationState, setPaginationState] = useState({ current: 1, pageSize: 10 });
  const [hoveredRowId, setHoveredRowId] = useState(null);

  const getShiftNumber = (shiftStr) => {
    return shiftStr === 'day' ? 0 : 1;
  };

  const convertApiDataToTableFormat = (apiData) => {
    if (!apiData || !apiData.dsrLogTechnicianMetadata) return [];

    const convertedData = apiData.dsrLogTechnicianMetadata.map((item) => ({
      id: item.id,
      name: `${item.userFirstName} ${item.userLastName}`,
      status: getStatusFromApiData(item),
      roles: (typeof item.role === 'string' && item.role.trim().length > 0) ? item.role : 'Select Role',
      hrs: item.hrs || 0,
      overTime: item.overTime || 0,
      apiData: item,
    }));

    return convertedData;
  };

  const getStatusFromApiData = (item) => {
    const allowedStatuses = new Set([
      'Mainline',
      'Offline',
      'Offshore',
      'Tie-In',
      'Standby',
      'Mobilize',
      'De-Mobilize',
    ]);

    if (typeof item.status === 'string' && item.status.length > 0) {
      const incoming = item.status.trim();
      for (const s of allowedStatuses) {
        if (s.toLowerCase() === incoming.toLowerCase()) return s;
      }
    }

    if (item.mobilize === 1) return 'Mobilize';
    if (item.deMobilize === 1) return 'De-Mobilize';

    if (
      item.mobilize === 0 &&
      item.onSite === 0 &&
      item.onLeave === 0 &&
      item.onVisit === 0 &&
      item.onTravel === 0 &&
      item.deMobilize === 0
    ) {
      return 'Select Status';
    }

    return 'Select Status';
  };

  const mapUiStatusToApi = (status) => {
    const allowed = new Set([
      'Mainline',
      'Offline',
      'Offshore',
      'Tie-In',
      'Standby',
      'Mobilize',
      'De-Mobilize',
    ]);
    if (allowed.has(status)) return status;
    return '';
  };

  const projectId = selectedProject?.id;
  const mainlineId = selectedMainline?.id;
  const logDateKey = useMemo(
    () => (selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : ''),
    [selectedDate],
  );

  // Fetch technician log when component mounts or dependencies change (use primitive deps to avoid duplicate GETs when parent passes new object references).
  useEffect(() => {
    if (!projectId || !mainlineId || !logDateKey || isSkipped) {
      return;
    }
    let userEmail = '';
    try {
      userEmail = JSON.parse(localStorage.getItem('user') || '{}').email || '';
    } catch (_) {
      return;
    }
    if (!userEmail) return;

    const shiftNumber = getShiftNumber(shift);
    onFetchTechnicianLog({
      projectId,
      logDate: logDateKey,
      userEmail,
      shift: shiftNumber,
      sowId: mainlineId,
    });
  }, [projectId, mainlineId, logDateKey, shift, isSkipped, onFetchTechnicianLog]);

  useEffect(() => {
    if (currentTechnicianLog) {
      const convertedData = convertApiDataToTableFormat(currentTechnicianLog);
      setRowData(convertedData);
      setComments(currentTechnicianLog.issues || '');
    }
  }, [currentTechnicianLog]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  const updateRowField = useCallback((id, field, value) => {
    setRowData((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  }, []);

  const totalRows = rowData.length;
  const { current: currentPage, pageSize } = paginationState;
  const maxPage = Math.max(1, totalRows === 0 ? 1 : Math.ceil(totalRows / pageSize));
  const page = Math.min(currentPage, maxPage);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rowData.slice(start, start + pageSize);
  }, [rowData, page, pageSize]);

  useEffect(() => {
    if (currentPage > maxPage) {
      setPaginationState((s) => ({ ...s, current: maxPage }));
    }
  }, [currentPage, maxPage]);

  const roleOptions = useMemo(() => {
    const collected = [];

    if (Array.isArray(currentTechnicianLog?.roles) && currentTechnicianLog.roles.length > 0) {
      collected.push(...currentTechnicianLog.roles.map((item) => item?.role).filter(Boolean));
    }

    if (Array.isArray(currentTechnicianLog?.dsrLogTechnicianMetadata)) {
      collected.push(
        ...currentTechnicianLog.dsrLogTechnicianMetadata
          .map((item) => item?.role)
          .filter((role) => typeof role === 'string' && role.trim().length > 0)
      );
    }

    if (Array.isArray(globalRoles)) {
      collected.push(
        ...globalRoles.map((r) => r?.name ?? r?.roleName ?? r?.role).filter(Boolean)
      );
    }

    const unique = [...new Set(collected.map((r) => String(r).trim()).filter(Boolean))];
    const activeRoleNames = rowData
      .map((row) => row.roles)
      .filter((role) => role && role !== 'Select Role');
    return buildDsrTechnicianRoleOptions(unique, getDsrTechnicianAllowedRoles(), activeRoleNames);
  }, [currentTechnicianLog, globalRoles, rowData]);

  useEffect(() => {
    onFetchRoles?.();
  }, [onFetchRoles]);

  const handleAddNewJobTitle = useCallback((rowId, name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;

    const exists = roleOptions.some(
      (r) => r !== 'Select Role' && String(r).toLowerCase() === trimmed.toLowerCase()
    );
    if (!exists) {
      onAddRole?.({ name: trimmed });
    }

    updateRowField(rowId, 'roles', trimmed);
  }, [onAddRole, updateRowField, roleOptions]);

  const isCellDisabled = skipped || isSubmitted;

  const handleCommentsChange = (e) => {
    setComments(e.target.value);
  };

  const handleSkipChange = (e) => {
    const newSkipState = e.target.checked;
    setSkipped(newSkipState);

    if (onSkipChange) {
      onSkipChange(e);
    } else {
      if (currentDsrLog?.id) {
        const submissionStatus = generateSubmissionStatus('technician', shift, true);
        const sectionsSkipped = {
          ...currentDsrLog.sectionsSkipped,
          isTechnicianLogSkippedDayShift:
            shift === 'day'
              ? newSkipState
                ? 1
                : 0
              : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedDayShift || 0,
          isTechnicianLogSkippedNightShift:
            shift === 'night'
              ? newSkipState
                ? 1
                : 0
              : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedNightShift || 0,
        };

        onUpdateDsrLogSections({
          dsrLogId: currentDsrLog.id,
          submissionStatus: submissionStatus,
          sectionsSkipped: sectionsSkipped,
        });
      }
    }
  };

  const generateSubmissionStatus = (section, sh, submittedFlag = true) => {
    const shiftKeySuffix = sh === 'day' ? 'Dayshift' : 'Nightshift';
    let submittedKey;

    switch (section) {
      case 'technician':
        submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
        break;
      case 'downtime':
        submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
        break;
      case 'consumables':
        submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
        break;
      case 'equipment':
        submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'ehs':
        submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
        break;
      case 'material':
        submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'production':
        submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
        break;
      default:
        return {};
    }

    return { [submittedKey]: submittedFlag };
  };

  const handleSave = async () => {
    if (skipped) {
      if (onComplete) onComplete(true, 'technician');
      return;
    }

    try {
      const dsrLogTechnicianMetadata = rowData.map((row) => {
        const statusString = mapUiStatusToApi(row.status);
        const roleString = (row.roles && row.roles !== 'Select Role') ? String(row.roles).toLowerCase() : '';
        return {
          id: row.id,
          hrs: row.hrs || 0,
          overTime: row.overTime || 0,
          userEmail: row.apiData?.userEmail,
          userFirstName: row.apiData?.userFirstName,
          userLastName: row.apiData?.userLastName,
          userProjectId: row.apiData?.userProjectId,
          status: statusString,
          role: roleString,
        };
      });

      const payload = {
        dsrLogTechnician: {
          id: currentTechnicianLog?.id,
          dsrLogId: currentDsrLog?.id,
          shift: getShiftNumber(shift),
          issues: comments,
          dsrLogTechnicianMetadata,
        },
      };

      await onUpdateTechnicianLog(payload);

      const submissionStatus = generateSubmissionStatus('technician', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isTechnicianLogSkippedDayShift:
          shift === 'day' ? 0 : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedDayShift || 0,
        isTechnicianLogSkippedNightShift:
          shift === 'night'
            ? 0
            : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedNightShift || 0,
      };

      await onUpdateDsrLogSections({
        dsrLogId: currentDsrLog?.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped,
      });

      if (onComplete) onComplete(true, 'technician');

      if (selectedProject && selectedMainline && selectedDate) {
        const userEmail = JSON.parse(localStorage.getItem('user')).email;
        const logDate = moment(selectedDate).format('YYYY-MM-DD');
        const shiftNumber = getShiftNumber(shift);

        onFetchTechnicianLog({
          projectId: selectedProject.id,
          logDate: logDate,
          userEmail: userEmail,
          shift: shiftNumber,
          sowId: selectedMainline.id,
        });
      }
    } catch (error) {
      console.error('Error saving technician log:', error);
      notification.error({
        message: 'Save Failed',
        description: 'Failed to save technician log data. Please try again.',
      });
    }
  };

  return (
    <div className="modern-dsr-tech-log-section">
      {isShiftSkipped ? (
        <div className="modern-dsr-tech-log-skipped-banner">
          <strong>
            {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
          </strong>
          <p>
            This section is disabled because the {shift} shift has been skipped.
          </p>
        </div>
      ) : (
        <Spin
          spinning={isTechnicianLogLoading}
          indicator={(
            <span className="modern-dsr-tech-log-spin" aria-hidden>
              <Loader2 className="modern-dsr-tech-log-spin-icon" size={28} strokeWidth={2} />
            </span>
          )}
          tip="Loading technician log data..."
          size="large"
        >
          <div className="modern-dsr-tech-log-card">
            <div className="modern-dsr-tech-log-card-head">
              <div className="modern-dsr-tech-log-card-title-block">
                <div className="modern-dsr-tech-log-card-icon" aria-hidden>
                  <Users size={22} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="modern-dsr-tech-log-card-title">Technician log</h3>
                  <p className="modern-dsr-tech-log-card-sub">Log team members, job title, status, and shift hours.</p>
                </div>
              </div>
            </div>

            <div className="modern-dsr-tech-table" role="table" aria-label="Technician log">
              <div className="modern-dsr-tech-thead" role="row">
                <div className="modern-dsr-tech-th" role="columnheader">Name</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Job title</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Status</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Hrs</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Over time</div>
              </div>

              {pagedRows.length === 0 ? (
                <div className="modern-dsr-tech-empty">No technicians loaded.</div>
              ) : (
                pagedRows.map((row) => (
                  <div
                    key={row.id}
                    className="modern-dsr-tech-row"
                    role="row"
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    data-active={hoveredRowId === row.id ? 'true' : undefined}
                  >
                    <div className="modern-dsr-tech-td modern-dsr-tech-td--name" role="cell">
                      <span className="modern-dsr-tech-name-text" title={row.name}>
                        {row.name}
                      </span>
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <TechnicianLogCustomSelect
                        id={`dd-role-${row.id}`}
                        value={row.roles}
                        options={roleOptions}
                        onChange={(v) => updateRowField(row.id, 'roles', v)}
                        onAddRole={(name) => handleAddNewJobTitle(row.id, name)}
                        disabled={isCellDisabled}
                        placeholder="Select Role"
                        showSearch
                        sortOptions
                      />
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <TechnicianLogCustomSelect
                        id={`dd-status-${row.id}`}
                        value={row.status}
                        options={STATUS_SELECT_OPTIONS}
                        onChange={(v) => updateRowField(row.id, 'status', v)}
                        disabled={isCellDisabled}
                        placeholder="Select Status"
                        showSearch={false}
                      />
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-tech-td--num" role="cell">
                      <div className="modern-dsr-tech-hours-wrap">
                        <InputNumber
                          className="modern-dsr-tech-inputnum"
                          value={row.hrs}
                          min={0}
                          onChange={(v) => updateRowField(row.id, 'hrs', v == null ? 0 : v)}
                          disabled={isCellDisabled}
                        />
                      </div>
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-tech-td--num" role="cell">
                      <div className="modern-dsr-tech-hours-wrap">
                        <InputNumber
                          className="modern-dsr-tech-inputnum"
                          value={row.overTime}
                          min={0}
                          onChange={(v) => updateRowField(row.id, 'overTime', v == null ? 0 : v)}
                          disabled={isCellDisabled}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modern-dsr-tech-log-pagination-bar">
              <Pagination
                className="heat-profile-pagination modern-dsr-tech-heat-profile-pagination"
                current={page}
                pageSize={pageSize}
                total={totalRows}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                showTotal={(t, range) =>
                  t > 0 ? `${range[0]}-${range[1]} of ${t}` : `0-0 of ${t}`
                }
                onChange={(p, newSize) => {
                  setPaginationState((s) => {
                    if (newSize !== s.pageSize) {
                      return { current: 1, pageSize: newSize };
                    }
                    return { ...s, current: p };
                  });
                }}
              />
            </div>

            <div className="modern-dsr-tech-log-issues-block">
              <div className="modern-dsr-tech-log-issues-label">Issues</div>
              <TextArea
                className="modern-dsr-tech-log-issues-textarea"
                rows={3}
                placeholder="Please add your issues here"
                value={comments}
                onChange={handleCommentsChange}
                disabled={skipped || isSubmitted}
              />
            </div>
          </div>
        </Spin>
      )}

      <div className="modern-dsr-tech-log-form-footer" aria-label="Form actions">
        <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
        <CrceButton
          label={isUpdatingTechnicianLog ? 'Saving…' : 'Save & Next'}
          variant="primary"
          size="medium"
          icon={Save}
          onClick={handleSave}
          disabled={isUpdatingTechnicianLog || isSubmitted || isShiftSkipped}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ dsrReducer, rolesReduceer }) => ({
  isTechnicianLogLoading: dsrReducer.isTechnicianLogLoading,
  isUpdatingTechnicianLog: dsrReducer.isUpdatingTechnicianLog,
  currentTechnicianLog: dsrReducer.currentTechnicianLog,
  globalRoles: get(rolesReduceer, 'roles', []),
});

const mapDispatchToProps = {
  onFetchTechnicianLog: fetchTechnicianLog,
  onUpdateTechnicianLog: updateTechnicianLog,
  onUpdateDsrLogSections: updateDsrLogSections,
  onAddRole: addRole,
  onFetchRoles: fetchRoles,
};

export default connect(mapStateToProps, mapDispatchToProps)(TechnicianLogSection);
